/**
 * TemplatesQuickWidget Component
 * 
 * Compact widget displaying featured templates with horizontal scroll.
 * Used on trips homepage to promote template usage.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { View, ScrollView, Pressable, ActivityIndicator, useWindowDimensions } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { SubHeading, Body, Caption } from '../ui/Text';
import { Card } from '../ui/Card';
import { TemplateCard } from './TemplateCard';
import { Button } from '../ui/Button';
import { fetchTripTemplates } from '@/services/tripTemplates';
import type { TripTemplate } from '@/types/tripTemplates';

const FEATURED_TEMPLATES_FETCH_TIMEOUT_MS = 12_000;

type TemplatesQuickWidgetProps = {
  onSelectTemplate: (template: TripTemplate, source?: 'browser' | 'widget') => void;
  onBrowseAll: () => void;
  refreshTrigger?: number;
};

export function TemplatesQuickWidget({ onSelectTemplate, onBrowseAll, refreshTrigger }: TemplatesQuickWidgetProps) {
  const theme = useTheme();
  const { width: windowWidth } = useWindowDimensions();
  const stackTemplateHeader = windowWidth < 420;
  const [featuredTemplates, setFeaturedTemplates] = useState<TripTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const fetchGenerationRef = useRef(0);

  const containerStyle = {
    backgroundColor: theme.colors.backgroundSecondary,
    borderColor: theme.colors.borderMuted,
  } as const;

  const loadFeaturedTemplates = useCallback(async () => {
    const gen = ++fetchGenerationRef.current;
    try {
      setLoading(true);
      const templates = await Promise.race([
        fetchTripTemplates(true, { skipImageBackfill: true }),
        new Promise<TripTemplate[]>((_, reject) => {
          setTimeout(
            () => reject(new Error('fetch_trip_templates_timeout')),
            FEATURED_TEMPLATES_FETCH_TIMEOUT_MS,
          );
        }),
      ]);
      if (gen !== fetchGenerationRef.current) return;
      setFeaturedTemplates(templates.filter((t) => t.featured).slice(0, 3));
    } catch (error) {
      console.error('[TemplatesQuickWidget] Error loading templates:', error);
      if (gen !== fetchGenerationRef.current) return;
      setFeaturedTemplates([]);
    } finally {
      if (gen === fetchGenerationRef.current) {
        setLoading(false);
      }
    }
  }, []);

  // Single effect: parent passes refreshTrigger (default 0) so we must not also mount-fetch in a second effect,
  // or we run two concurrent loads and can keep the spinner up until the slowest completes.
  useEffect(() => {
    void loadFeaturedTemplates();
  }, [refreshTrigger, loadFeaturedTemplates]);

  if (loading) {
    return (
      <Card variant="outlined" padding={theme.spacing(2)} style={containerStyle}>
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: theme.spacing(1) }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
            <View
              style={{
                width: 32,
                height: 32,
                borderRadius: theme.radii.pill,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: theme.colors.primary + '12',
              }}
            >
              <Ionicons name="map-outline" size={18} color={theme.colors.primary} />
            </View>
            <SubHeading>Start with a Template</SubHeading>
          </View>
        </View>
        <View style={{ alignItems: 'center', padding: theme.spacing(2) }}>
          <ActivityIndicator size="small" color={theme.colors.primary} />
        </View>
      </Card>
    );
  }

  if (featuredTemplates.length === 0) {
    return (
      <Card variant="outlined" padding={theme.spacing(2)} style={containerStyle}>
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: theme.spacing(1) }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
            <View
              style={{
                width: 32,
                height: 32,
                borderRadius: theme.radii.pill,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: theme.colors.primary + '12',
              }}
            >
              <Ionicons name="map-outline" size={18} color={theme.colors.primary} />
            </View>
            <SubHeading>Start with a Template</SubHeading>
          </View>
        </View>

        <Body color={theme.colors.textSecondary} style={{ marginBottom: theme.spacing(1.25) }}>
          Browse curated itineraries to jumpstart your planning
        </Body>

        <Button
          label="Browse Templates"
          variant="secondary"
          onPress={onBrowseAll}
          icon={<Ionicons name="map-outline" size={18} color={theme.colors.primary} />}
        />
      </Card>
    );
  }

  return (
    <Card variant="outlined" padding={theme.spacing(2)} style={containerStyle}>
      {/* Header — title truncates; narrow screens stack so "Browse all" never overlaps */}
      <View
        style={{
          flexDirection: stackTemplateHeader ? 'column' : 'row',
          alignItems: stackTemplateHeader ? 'stretch' : 'center',
          justifyContent: 'space-between',
          marginBottom: theme.spacing(1),
          gap: theme.spacing(1),
        }}
      >
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: theme.spacing(1),
            flex: stackTemplateHeader ? undefined : 1,
            flexShrink: 1,
            minWidth: 0,
          }}
        >
          <View
            style={{
              width: 32,
              height: 32,
              borderRadius: theme.radii.pill,
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              backgroundColor: theme.colors.primary + '12',
            }}
          >
            <Ionicons name="map-outline" size={18} color={theme.colors.primary} />
          </View>
          <View style={{ flex: 1, minWidth: 0 }}>
            <SubHeading numberOfLines={1} ellipsizeMode="tail" style={{ flexShrink: 1 }}>
              Start with a Template
            </SubHeading>
          </View>
        </View>
        <Pressable
          onPress={onBrowseAll}
          hitSlop={8}
          accessibilityRole="button"
          accessibilityLabel="Browse all templates"
          accessibilityHint="Opens the template browser"
          style={({ pressed }) => ({
            flexShrink: 0,
            flexGrow: 0,
            alignSelf: stackTemplateHeader ? 'flex-end' : undefined,
            flexDirection: 'row',
            alignItems: 'center',
            paddingLeft: theme.spacing(0.5),
            paddingRight: theme.spacing(0.5),
            paddingVertical: theme.spacing(0.75),
            borderRadius: theme.radii.pill,
            backgroundColor: pressed ? theme.colors.overlay : 'transparent',
          })}
        >
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5), paddingRight: 2 }}>
            <Caption color={theme.colors.primary} style={{ fontWeight: '700' }}>
              Browse all
            </Caption>
            <Ionicons name="chevron-forward" size={18} color={theme.colors.primary} />
          </View>
        </Pressable>
      </View>

      {/* Description */}
      <Body color={theme.colors.textSecondary} style={{ marginBottom: theme.spacing(1) }}>
        Get inspired with curated itineraries and estimated budgets
      </Body>

      {/* Templates Horizontal Scroll */}
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        nestedScrollEnabled
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={{
          gap: theme.spacing(1),
          paddingRight: theme.spacing(1),
          paddingBottom: theme.spacing(0.25),
        }}
      >
        {featuredTemplates.map((template) => (
          <View key={template.id} style={{ width: 184 }}>
            <TemplateCard
              template={template}
              onPress={(selected) => onSelectTemplate(selected, 'widget')}
              variant="compact"
            />
          </View>
        ))}
      </ScrollView>
    </Card>
  );
}

